import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import {
    FileUploadStatus,
    FileUploadItem,
    FINISHED_SOURCE_STATUSES,
    PREPARING_SOURCE_STATUSES,
} from '@/types/source-creation';
import type { UploadFileResponse } from '@/types/responses';
import { useSourceCreationStore } from '@/stores/source-creation.store';
import { EngineService } from '@/services/engine';
import {
    CACHE_KEYS,
    getReportPipelineStatusCacheKey,
} from '@/constants/cache-keys';
import { revalidateSourceFolders } from '@/hooks/useSourceFoldersInfinite';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { revalidateIdentityViews } from '@/utils/swr';
import { revalidateSourceList } from './useSourceListInfinite';

const MAX_CONCURRENT = 3;
const MAX_RETRIES = 2;
const AWAITING_BACKEND_POLL_INTERVAL_MS = 3000;

function resolveCreatedSourceId(
    response: UploadFileResponse,
): string | undefined {
    if (response.id != null && response.id !== '') {
        return response.id;
    }
    const segments =
        response.uri?.split('/').filter((segment) => segment.length > 0) ?? [];
    if (segments.length < 2) {
        return undefined;
    }
    return segments[segments.length - 2];
}

function shouldPollFileUploadItem(
    item: FileUploadItem,
): item is FileUploadItem & { createdSourceId: string } {
    if (item.status !== FileUploadStatus.COMPLETED) return false;
    if (!item.createdSourceId) return false;
    if (item.sourceStatus === undefined) return true;
    return !FINISHED_SOURCE_STATUSES.has(item.sourceStatus);
}

class AwaitingBackendPoller {
    private tail: Promise<void> = Promise.resolve();

    enqueue(): Promise<void> {
        const scheduled = this.tail.then(() => this.runOnce());
        this.tail = scheduled.catch(() => {});
        return scheduled;
    }

    private async runOnce(): Promise<void> {
        const items = useSourceCreationStore.getState().fileItems;
        const toPoll = items.filter(shouldPollFileUploadItem);
        if (toPoll.length === 0) {
            return;
        }

        let shouldInvalidateSourcesList = false;
        let shouldInvalidateIdentities = false;
        const foldersToInvalidate = new Set<string>();

        await Promise.all(
            toPoll.map(async (entry) => {
                try {
                    const source = await EngineService.getSource(
                        entry.createdSourceId,
                    );
                    const latest = useSourceCreationStore
                        .getState()
                        .fileItems.find(
                            (candidate) => candidate.id === entry.id,
                        );
                    if (!latest || !shouldPollFileUploadItem(latest)) {
                        return;
                    }

                    const wasPreparing =
                        latest.sourceStatus === undefined ||
                        PREPARING_SOURCE_STATUSES.has(latest.sourceStatus);

                    useSourceCreationStore.getState().updateFileItem(entry.id, {
                        sourceStatus: source.status,
                        sourceProgress: source.progress,
                    });

                    if (
                        wasPreparing &&
                        !PREPARING_SOURCE_STATUSES.has(source.status)
                    ) {
                        shouldInvalidateSourcesList = true;
                        if (latest.targetFolderId) {
                            foldersToInvalidate.add(latest.targetFolderId);
                        }
                    }

                    if (FINISHED_SOURCE_STATUSES.has(source.status)) {
                        shouldInvalidateSourcesList = true;
                        shouldInvalidateIdentities = true;
                    }
                } catch (error) {
                    console.warn(
                        `Failed to poll source status for ${entry.createdSourceId}`,
                        error,
                    );
                }
            }),
        );

        if (shouldInvalidateSourcesList) {
            await revalidateSourceList();
            await revalidateSourceFolders();
            await Promise.all(
                Array.from(foldersToInvalidate).map((folderId) =>
                    mutate(
                        (key) =>
                            Array.isArray(key) &&
                            key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN &&
                            key[1] === folderId,
                    ),
                ),
            );
        }

        if (shouldInvalidateIdentities) {
            await revalidateIdentityViews();
        }
    }
}

const awaitingBackendPoller = new AwaitingBackendPoller();

export function useFileUpload() {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const fileItems = useSourceCreationStore((state) => state.fileItems);
    const updateFileItem = useSourceCreationStore(
        (state) => state.updateFileItem,
    );
    const uploadingRef = useRef(new Set<string>());

    useEffect(() => {
        const pending = fileItems.filter(
            (item) => item.status === FileUploadStatus.PENDING,
        );
        const uploading = fileItems.filter(
            (item) => item.status === FileUploadStatus.UPLOADING,
        );

        const availableSlots = MAX_CONCURRENT - uploading.length;
        const toStart = pending.slice(0, availableSlots);

        toStart.forEach((item: FileUploadItem) => {
            if (!uploadingRef.current.has(item.id)) {
                uploadingRef.current.add(item.id);
                upload(item);
            }
        });
    }, [fileItems, updateFileItem]);

    const awaitingWakeKeyRef = useRef('');

    useEffect(() => {
        const unsub = useSourceCreationStore.subscribe(() => {
            const awaiting = useSourceCreationStore
                .getState()
                .fileItems.filter(shouldPollFileUploadItem);
            const wakeKey = awaiting
                .map((item) => `${item.id}:${item.createdSourceId ?? ''}`)
                .sort()
                .join(';');
            if (wakeKey === awaitingWakeKeyRef.current) {
                return;
            }
            awaitingWakeKeyRef.current = wakeKey;
            if (awaiting.length > 0) {
                void awaitingBackendPoller.enqueue();
            }
        });

        void awaitingBackendPoller.enqueue();
        const intervalId = window.setInterval(
            () => void awaitingBackendPoller.enqueue(),
            AWAITING_BACKEND_POLL_INTERVAL_MS,
        );

        return () => {
            unsub();
            window.clearInterval(intervalId);
        };
    }, []);

    async function upload(item: FileUploadItem) {
        updateFileItem(item.id, {
            status: FileUploadStatus.UPLOADING,
            progress: 0,
            createdSourceId: undefined,
            sourceStatus: undefined,
            sourceProgress: undefined,
        });

        try {
            const analysis = item.targetReportId
                ? {
                      faceRecognition: true,
                      licensePlateRecognition: false,
                      opticalCharacterRecognition: false,
                      processingFps: 15,
                  }
                : {
                      faceRecognition: item.faceRecognition,
                      licensePlateRecognition: item.licensePlateRecognition,
                      opticalCharacterRecognition:
                          item.opticalCharacterRecognition,
                      processingFps: item.analysisFramesPerSecond,
                  };

            const response = await EngineService.uploadSource(
                item.file,
                item.fileName,
                (percent) => {
                    updateFileItem(item.id, { progress: percent });
                },
                analysis,
                item.targetReportId,
            );

            uploadingRef.current.delete(item.id);

            const createdSourceId = resolveCreatedSourceId(response);

            if (createdSourceId && item.targetFolderId) {
                try {
                    await EngineService.addSourceFolderSources(
                        item.targetFolderId,
                        {
                            sourceIds: [createdSourceId],
                        },
                    );
                } catch (error) {
                    console.warn(
                        `Failed to add source ${createdSourceId} to folder ${item.targetFolderId}`,
                        error,
                    );
                    toast.error(t('toast.addToFolderError'));
                }
            }

            updateFileItem(item.id, {
                status: FileUploadStatus.COMPLETED,
                progress: 100,
                createdSourceId,
                sourceStatus: undefined,
                sourceProgress: undefined,
            });

            await awaitingBackendPoller.enqueue();

            await revalidateSourceFolders();
            if (item.targetReportId) {
                await mutate(
                    getReportPipelineStatusCacheKey(item.targetReportId),
                );
            }
        } catch (error: unknown) {
            uploadingRef.current.delete(item.id);
            const errorMsg =
                error instanceof Error ? error.message : 'Upload failed';

            if (item.retryCount < MAX_RETRIES) {
                const delay = Math.pow(2, item.retryCount) * 1000;
                await new Promise((resolve) => setTimeout(resolve, delay));

                updateFileItem(item.id, {
                    retryCount: item.retryCount + 1,
                    status: FileUploadStatus.PENDING,
                    createdSourceId: undefined,
                    sourceStatus: undefined,
                    sourceProgress: undefined,
                });
            } else {
                updateFileItem(item.id, {
                    status: FileUploadStatus.FAILED,
                    error: errorMsg,
                });
            }
        }
    }
}
